import { useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { fetchData } from "@/lib/helper";
import { getJourneyToken } from "@/lib/journeyToken";
import { resolveLender } from "@/lib/lender";
import { DEFAULT_LENDER_PROFILES } from "@/lib/lenderDefaults";
import {
  JOURNEY_LENDER_RETRY_DELAYS_MS,
  isKnownLenderKey,
  lenderFromJourneyResponse,
  pickJourneyLenderKey,
} from "@/lib/journeyLenderCore";
import { lenderConfigAtom } from "@/recoil/atom/lenderConfigAtom";
import { journeyLenderAtom } from "@/recoil/atom/journeyLenderAtom";

/**
 * The CURRENT JOURNEY's lender profile (branding + consent copy), or null while it is not known.
 * Journey screens use this instead of `useLender` and render nothing lender-specific — no
 * "powered by" logo, no consent that names a lender — while it is null. See journeyLenderCore.js
 * for why a journey screen must never fall back to the primary lender.
 *
 * `knownKey` is the lender the screen already read from its own journey data, if any. Without
 * it the hook asks `journey/full` itself, and keeps asking for a short while: the first screen
 * after the journey is created can ask before the journey (or its session token) exists.
 */
export function useJourneyLender(knownKey) {
  const lenderConfig = useRecoilValue(lenderConfigAtom);
  const [cached, setCached] = useRecoilState(journeyLenderAtom);
  const owner = getJourneyToken();
  const key = pickJourneyLenderKey({ known: knownKey, cached, owner });

  // Hand what this screen learned to the next one.
  useEffect(() => {
    if (!knownKey || !owner) return;
    if (cached.owner === owner && cached.key === knownKey) return;
    setCached({ owner, key: knownKey });
  }, [knownKey, owner, cached.owner, cached.key, setCached]);

  useEffect(() => {
    if (key) return undefined;
    let cancelled = false;
    let timer = null;
    const ask = async (attempt) => {
      let response = null;
      try {
        response = await fetchData("journey/full");
      } catch {
        // Treated like any unanswered ask: wait, then ask again.
      }
      const found = lenderFromJourneyResponse(response);
      if (cancelled) return;
      // Read the session again: it can be issued while this screen is already up.
      const session = getJourneyToken();
      if (found && session) {
        setCached({ owner: session, key: found });
        return;
      }
      const wait = JOURNEY_LENDER_RETRY_DELAYS_MS[attempt];
      if (wait !== undefined) timer = setTimeout(() => ask(attempt + 1), wait);
    };
    ask(0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [key, owner, setCached]);

  if (!isKnownLenderKey(lenderConfig?.lenders, DEFAULT_LENDER_PROFILES, key)) {
    return null;
  }
  return resolveLender(lenderConfig, key);
}
